import { useEffect, useState } from 'react'

/** Eases out, so the count slows into its total rather than stopping dead. */
function eased(progress: number): number {
  return 1 - Math.pow(1 - progress, 3)
}

/**
 * A number arriving rather than appearing: the work is being done as you read.
 * Waits for `run`, since a counter that starts on mount has finished long
 * before a line that mounts hidden is ever shown. It keeps its total when
 * `run` goes false again, so it does not reset while fading out.
 */
export function CountUp({
  to,
  duration = 1300,
  run = true,
}: {
  to: number
  duration?: number
  run?: boolean
}) {
  const [value, setValue] = useState(0)

  useEffect(() => {
    if (!run) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setValue(to)
      return
    }
    const start = performance.now()
    let frame = requestAnimationFrame(function tick(now) {
      const progress = Math.min((now - start) / duration, 1)
      setValue(Math.round(to * eased(progress)))
      if (progress < 1) frame = requestAnimationFrame(tick)
    })
    return () => cancelAnimationFrame(frame)
  }, [run, to, duration])

  // Fixed width at the final size with tabular digits: the strip is
  // right-aligned, so a number that grows would drag the words along with it.
  return (
    <span
      className="inline-block text-right tabular-nums"
      style={{ minWidth: `${to.toLocaleString().length}ch` }}
    >
      {value.toLocaleString()}
    </span>
  )
}
